import type { TFunction } from 'i18next';

export interface BrandEdgeScoreInputs {
  awareness: number;
  usage: number;
  loyalty: number;
  primaryShare: number;
  switchingRisk: number;
}

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));
const round = (value: number): number => Math.round(value);

export const computeBrandEdgeScore = (metrics: BrandEdgeScoreInputs): number => {
  const score =
    metrics.awareness * 0.3 +
    metrics.usage * 0.3 +
    metrics.loyalty * 0.2 +
    metrics.primaryShare * 0.1 -
    metrics.switchingRisk * 0.1;

  return clamp(round(score), 0, 100);
};

export const describeBrandEdgeScore = (score: number): string => {
  if (score >= 80) return 'Dominant brand position';
  if (score >= 60) return 'Strong but contested';
  if (score >= 40) return 'Competitive pressure';
  if (score >= 20) return 'Weak market position';
  return 'Critical risk';
};

export const brandEdgeScorePositionKey = (score: number): string => {
  if (score >= 80) return 'dominant';
  if (score >= 60) return 'strong';
  if (score >= 40) return 'competitive';
  if (score >= 20) return 'weak';
  return 'critical';
};

export const summarizeBrandEdgeDrivers = (metrics: BrandEdgeScoreInputs, t?: TFunction): string => {
  const tr = (key: string, fallback: string): string => (t ? t(key, fallback) : fallback);

  // ── Sentence 1: awareness context ──────────────────────────────────────────
  const awarenessSentence =
    metrics.awareness >= 60
      ? tr('insights:brandEdgeDriver_awareHigh', 'The brand is well known in the market and awareness is not the primary challenge.')
      : metrics.awareness >= 40
      ? tr('insights:brandEdgeDriver_awareMid', 'The brand has moderate market reach, though there is room to grow the awareness base.')
      : tr('insights:brandEdgeDriver_awareLow', 'Brand awareness is limited, which constrains all downstream metrics.');

  // ── Sentence 2: main opportunity ────────────────────────────────────────────
  const belowThreshold = [
    { key: 'brandEdgeMetric_usage' as const, fallback: 'usage', value: metrics.usage },
    { key: 'brandEdgeMetric_loyalty' as const, fallback: 'loyalty', value: metrics.loyalty },
    { key: 'brandEdgeMetric_primaryBankShare' as const, fallback: 'primary bank share', value: metrics.primaryShare },
  ].filter((c) => c.value < 50);

  let opportunitySentence: string;
  if (belowThreshold.length === 0) {
    opportunitySentence = tr('insights:brandEdgeDriver_allAbove', 'All core components are performing above average, giving the brand a solid platform.');
  } else if (belowThreshold.length === 1) {
    const metric = tr(`kpi:${belowThreshold[0].key}`, belowThreshold[0].fallback);
    opportunitySentence = tr('insights:brandEdgeDriver_oneBelow', `The largest opportunity is improving ${belowThreshold[0].fallback} to push the score higher.`)
      .replace('{{metric}}', metric);
  } else {
    const m1 = tr(`kpi:${belowThreshold[0].key}`, belowThreshold[0].fallback);
    const m2 = tr(`kpi:${belowThreshold[1].key}`, belowThreshold[1].fallback);
    opportunitySentence = tr('insights:brandEdgeDriver_twoBelow', `The largest opportunity lies in improving ${belowThreshold[0].fallback} and ${belowThreshold[1].fallback}, both of which are below the level needed for a higher score.`)
      .replace('{{metric1}}', m1)
      .replace('{{metric2}}', m2);
  }

  // ── Sentence 3 (optional): switching risk ──────────────────────────────────
  const riskSentence =
    metrics.switchingRisk > 20
      ? ` ${tr('insights:brandEdgeDriver_switchingRisk', `Competitive switching pressure at ${metrics.switchingRisk}% is also adding downward weight to the score.`).replace('{{rate}}', String(metrics.switchingRisk))}`
      : '';

  return `${awarenessSentence} ${opportunitySentence}${riskSentence}`.trim();
};
